import React from 'react';
import BottomTabNav from '../shared/components/BottomTabNav';
import Footer from '../shared/components/Footer';
import './CustomerTermsOfService.css';

const CustomerTermsOfService = () => {
  return (
    <div className="App">
      <div className="terms-container">
        <div className="terms-content">
          {/* Header */}
          <div className="terms-header">
            <h1 className="terms-title">Terms of Service</h1>
            <p className="terms-subtitle">Please read these terms carefully</p>
            <p className="last-updated">Last updated: January 1, 2024</p>
          </div>

          {/* Introduction */}
          <div className="terms-section">
            <h2 className="section-title">Agreement to Terms</h2>
            <div className="content-card">
              <p>
                Welcome to ZepEat! These Terms of Service ("Terms") govern your use of our food delivery platform, 
                including our mobile application and website. By accessing or using ZepEat, you agree to be bound by 
                these Terms. If you do not agree to these Terms, please do not use our service.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you and ZepEat. We may modify these Terms 
                at any time, and such modifications will be effective immediately upon posting on our platform.
              </p>
            </div>
          </div>

          {/* Service Description */}
          <div className="terms-section">
            <h2 className="section-title">Service Description</h2>
            <div className="content-card">
              <div className="service-grid">
                <div className="service-item">
                  <div className="service-icon">🍕</div>
                  <div className="service-content">
                    <h3>Food Ordering</h3>
                    <p>Browse menus from local restaurants and place orders for delivery or pickup.</p>
                  </div>
                </div>
                
                <div className="service-item">
                  <div className="service-icon">🛒</div>
                  <div className="service-content">
                    <h3>Grocery Delivery</h3>
                    <p>Order fresh groceries and daily essentials from partner stores.</p>
                  </div>
                </div>
                
                <div className="service-item">
                  <div className="service-icon">🚚</div>
                  <div className="service-content">
                    <h3>Delivery Service</h3>
                    <p>Fast and reliable delivery through our network of professional riders.</p>
                  </div>
                </div>
                
                <div className="service-item">
                  <div className="service-icon">💳</div>
                  <div className="service-content">
                    <h3>Payment Processing</h3>
                    <p>Secure payment processing for all transactions through multiple payment methods.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User Responsibilities */}
          <div className="terms-section">
            <h2 className="section-title">User Responsibilities</h2>
            <div className="content-card">
              <div className="responsibility-list">
                <div className="responsibility-item">
                  <div className="responsibility-icon">✅</div>
                  <div className="responsibility-content">
                    <h3>Account Information</h3>
                    <p>Provide accurate, current, and complete information when creating your account and keep it updated.</p>
                  </div>
                </div>
                
                <div className="responsibility-item">
                  <div className="responsibility-icon">🔒</div>
                  <div className="responsibility-content">
                    <h3>Account Security</h3>
                    <p>Maintain the confidentiality of your account credentials and notify us immediately of any unauthorized access.</p>
                  </div>
                </div>
                
                <div className="responsibility-item">
                  <div className="responsibility-icon">⚖️</div>
                  <div className="responsibility-content">
                    <h3>Legal Compliance</h3>
                    <p>Use our service only for lawful purposes and in accordance with applicable laws and regulations.</p>
                  </div>
                </div>
                
                <div className="responsibility-item">
                  <div className="responsibility-icon">🚫</div>
                  <div className="responsibility-content">
                    <h3>Prohibited Activities</h3>
                    <p>Do not engage in fraudulent activities, abuse our platform, or violate the rights of others.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ordering and Payment */}
          <div className="terms-section">
            <h2 className="section-title">Ordering and Payment</h2>
            <div className="content-card">
              <div className="policy-grid">
                <div className="policy-item">
                  <h3>📋 Order Placement</h3>
                  <ul>
                    <li>Orders are subject to availability and restaurant acceptance</li>
                    <li>Minimum order values may apply for certain restaurants</li>
                    <li>We reserve the right to refuse or cancel orders</li>
                    <li>Order confirmation does not guarantee acceptance</li>
                  </ul>
                </div>
                
                <div className="policy-item">
                  <h3>💰 Pricing and Fees</h3>
                  <ul>
                    <li>All prices are displayed in local currency including applicable taxes</li>
                    <li>Delivery fees, service charges, and tips are additional</li>
                    <li>Prices may vary between restaurants and locations</li>
                    <li>Dynamic pricing may apply during peak hours</li>
                  </ul>
                </div>
                
                <div className="policy-item">
                  <h3>💳 Payment Terms</h3>
                  <ul>
                    <li>Payment is due at the time of order placement</li>
                    <li>We accept major credit cards, debit cards, and digital wallets</li>
                    <li>Cash payments may be available for certain orders</li>
                    <li>Failed payments will result in order cancellation</li>
                  </ul>
                </div>
                
                <div className="policy-item">
                  <h3>🔄 Refunds and Cancellations</h3>
                  <ul>
                    <li>Orders may be cancelled within 5 minutes of placement</li>
                    <li>Refunds are processed according to our refund policy</li>
                    <li>Restaurant cancellations result in full refunds</li>
                    <li>Quality issues may be eligible for partial or full refunds</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Terms */}
          <div className="terms-section">
            <h2 className="section-title">Delivery Terms</h2>
            <div className="content-card">
              <div className="delivery-terms">
                <div className="delivery-item">
                  <h3>🕐 Delivery Times</h3>
                  <p>Estimated delivery times are approximate and may vary due to weather, traffic, or high demand. We are not liable for delays beyond our control.</p>
                </div>
                
                <div className="delivery-item">
                  <h3>📍 Delivery Areas</h3>
                  <p>We deliver within specified service areas. Delivery availability may change based on location, time, and weather conditions.</p>
                </div>
                
                <div className="delivery-item">
                  <h3>👤 Delivery Requirements</h3>
                  <p>Someone must be available to receive the order at the delivery address. Valid ID may be required for age-restricted items.</p>
                </div>
                
                <div className="delivery-item">
                  <h3>📦 Order Handling</h3>
                  <p>We take reasonable care in handling your order, but we are not responsible for items once delivered to the specified address.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Liability and Disclaimers */}
          <div className="terms-section">
            <h2 className="section-title">Liability and Disclaimers</h2>
            <div className="content-card">
              <div className="disclaimer-list">
                <div className="disclaimer-item">
                  <div className="disclaimer-icon">⚠️</div>
                  <h3>Service Availability</h3>
                  <p>We do not guarantee uninterrupted service availability and may suspend service for maintenance or other reasons.</p>
                </div>
                
                <div className="disclaimer-item">
                  <div className="disclaimer-icon">🏪</div>
                  <h3>Restaurant Quality</h3>
                  <p>We are not responsible for food quality, preparation, or safety. Restaurants are independent third parties.</p>
                </div>
                
                <div className="disclaimer-item">
                  <div className="disclaimer-icon">💼</div>
                  <h3>Limited Liability</h3>
                  <p>Our liability is limited to the amount paid for the specific order in question, excluding consequential damages.</p>
                </div>
                
                <div className="disclaimer-item">
                  <div className="disclaimer-icon">🛡️</div>
                  <h3>Indemnification</h3>
                  <p>You agree to indemnify ZepEat against claims arising from your use of the service or violation of these Terms.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="terms-section">
            <h2 className="section-title">Intellectual Property</h2>
            <div className="content-card">
              <div className="ip-content">
                <div className="ip-item">
                  <h3>🏷️ ZepEat Property</h3>
                  <p>All content, trademarks, logos, and intellectual property on our platform are owned by ZepEat or our licensors. You may not use, reproduce, or distribute our intellectual property without permission.</p>
                </div>
                
                <div className="ip-item">
                  <h3>👤 User Content</h3>
                  <p>By submitting reviews, photos, or other content, you grant ZepEat a non-exclusive license to use, modify, and display such content for business purposes.</p>
                </div>
                
                <div className="ip-item">
                  <h3>🚫 Copyright Infringement</h3>
                  <p>We respect intellectual property rights and will respond to valid copyright infringement claims in accordance with applicable law.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Termination */}
          <div className="terms-section">
            <h2 className="section-title">Account Termination</h2>
            <div className="content-card">
              <div className="termination-grid">
                <div className="termination-item">
                  <div className="termination-icon">👤</div>
                  <h3>User Termination</h3>
                  <p>You may terminate your account at any time by contacting our support team or using account settings.</p>
                </div>
                
                <div className="termination-item">
                  <div className="termination-icon">🏢</div>
                  <h3>ZepEat Termination</h3>
                  <p>We may suspend or terminate your account for violations of these Terms or illegal activities.</p>
                </div>
                
                <div className="termination-item">
                  <div className="termination-icon">📊</div>
                  <h3>Effect of Termination</h3>
                  <p>Upon termination, your access will cease, but certain provisions of these Terms will survive.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Governing Law */}
          <div className="terms-section">
            <h2 className="section-title">Governing Law and Disputes</h2>
            <div className="content-card">
              <div className="legal-info">
                <div className="legal-item">
                  <h3>⚖️ Governing Law</h3>
                  <p>These Terms are governed by the laws of the State of New York, without regard to conflict of law principles.</p>
                </div>
                
                <div className="legal-item">
                  <h3>🏛️ Dispute Resolution</h3>
                  <p>Disputes will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.</p>
                </div>
                
                <div className="legal-item">
                  <h3>📍 Jurisdiction</h3>
                  <p>Any legal proceedings must be brought in the courts of New York County, New York.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="terms-section">
            <h2 className="section-title">Contact Information</h2>
            <div className="content-card">
              <p>If you have any questions about these Terms of Service, please contact us:</p>
              
              <div className="contact-info">
                <div className="contact-item">
                  <strong>Email:</strong> legal@zepeat.com
                </div>
                <div className="contact-item">
                  <strong>Phone:</strong> +1 (555) 123-4567
                </div>
                <div className="contact-item">
                  <strong>Address:</strong> 123 Food Street, City Center, New York, NY 10001
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer websiteType="customer" />
      <BottomTabNav />
    </div>
  );
};

export default CustomerTermsOfService;
